import type { UseFetchOptions } from 'nuxt/app';
import { hash } from 'ohash';
import { flatMapDeep } from 'lodash-es';
/**
 * Hash nested and MaybeRef'd objects in a fast and consistent way.
 * @param thing
 */
export const createKey = (thing: MaybeRefOrGetter<Readonly<object | any[]>>) => {
    return hash(
        flatMapDeep(
            toValue(thing),
            (value, index) => [index, toValue(value)]
        )
    );
};

type FetchError = {
    status: number;
    message: string;
}

export function useDefaultLaravelHeaders() {
    const { laravelApiToken } = useRuntimeConfig().public;

    const { jwtToken } = storeToRefs(useUserStore());

    return ref({
        'x-api-key': laravelApiToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ... jwtToken.value
            ? { Authorization: `Bearer ${jwtToken.value}` }
            : {}
    });
}

export async function useFetchLaravel<T = unknown, E = unknown>(
    path: MaybeRefOrGetter<string>,
    options?: RequestInit & { data?: Record<string, any> }
) {
    const data: Ref<T|null> = ref(null);
    const error: Ref<null|(FetchError & { data: E })> = ref(null);

    const parsedOptions: RequestInit = options || {};

    if (options?.data) {
        parsedOptions.body = JSON.stringify(options.data);
    }

    return fetch(useLaravelUrlPath(path).value, {
        headers: useDefaultLaravelHeaders().value,
        ...parsedOptions
    }).then(async(response) => {
        if (!response.ok) {
            error.value = {
                status: response.status,
                message: response.statusText,
                data: await response.json() as E
            };
        } else {
            try {
                data.value = await response.json();
            } catch {
                // ignore
            }
        }

        return {
            data,
            error
        };
    });
}

export function useLaravelUrlPath(path: MaybeRefOrGetter<string>)
{
    const { public: { laravelApiBaseURL } } = useRuntimeConfig();

    return computed(() => laravelApiBaseURL + toValue(path));
}

export function useAsyncFetchLaravel<T>(path: MaybeRefOrGetter<string>, fetchOptions: UseFetchOptions<T> = {}) {
    const options: UseFetchOptions<T> = {
        headers: useDefaultLaravelHeaders(),
        ...fetchOptions,
        $fetch,
    };

    return useFetch(useLaravelUrlPath(path), options);
}
